// ============================================================================
// NotificationsBell - a single "what needs my attention" surface for staff.
//
// Aggregates the actionable queues into one bell + dropdown so a user does not
// have to scan the whole rail. Each source is gated on a nav id the role can
// actually open (passed in as accessibleNavIds), and every count query is
// RLS-scoped to the practice, so no practice filter is needed here.
//
// Round 4 #3 (Oct 2026): the counts come from the shared shell cache
// (hooks/useShellCounts.js) instead of a private 60s poll. The Inbox row is
// the same number as the sidebar badge and the Inbox page subtitle (open
// unified items, inbox_feed_counts), not unread portal messages; insurance
// updates are the sidebar badge's own entry. Each other source is one shared
// query, so a re-render or a nav change never re-asks. A failed count says so
// in the dropdown (with Retry) rather than reading as zero.
//
// Round 7 #2 (Nov 2026): all of them are filled by the shell's one read
// (shell_counts, lib/shellCounts.js), polled once a minute by Layout; the
// bell has no timer of its own.
// ============================================================================
import { useState, useEffect, useRef, useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../auth/AuthProvider";
import { C, INBOX_DOCS_ONLY_ROLES } from "../lib/tokens";
import { SHELL_READS, readShellKey, shellKeyFor } from "../lib/shellCounts";
import { useInboxCounts } from "../hooks/useShellCounts";
import { IconBell, IconInbox, IconCreditCard, IconClock, IconReceipt, IconCheckSquare, IconCheck, IconFileText } from "./icons";
import { toast } from "./ToastProvider";
import { practiceTzOf } from "../lib/practiceDay";

// inbox: read through useInboxCounts (shared with the sidebar badge). Every
// other source is one shared count keyed by its scope (shellKeyFor); its own
// read (lib/shellCounts.js SHELL_READS: the HEAD counts that used to live
// here, unchanged) runs only when the shell's one read has no value for it.
const SOURCES = [
  { key: "inbox", nav: "inbox", notRoles: INBOX_DOCS_ONLY_ROLES, path: "/inbox", state: { view: "unified" }, label: "Open inbox items", Icon: IconInbox, shared: "inbox" },
  { key: "insurance_updates", nav: "insurance_updates", path: "/insurance-updates", state: { statusFilter: "Pending Review" }, label: "Insurance updates", Icon: IconCreditCard },
  { key: "waitlist", nav: "waitlist", path: "/waitlist", label: "Waitlist offers", Icon: IconClock },
  { key: "billing", nav: "billing", path: "/billing?tab=held", label: "Billing on hold", Icon: IconReceipt },
  // Aging unsigned notes: Draft / In Progress, visit at least 2 practice days
  // ago (fresh same-day drafts are expected and don't nag).
  { key: "unsigned_notes", nav: "clinical", path: "/clinical", state: { filter: "drafts" }, label: "Notes to sign", Icon: IconFileText },
  // Mine = assigned to me, or sitting unclaimed in my role's queue; snoozed
  // tasks are hidden until they wake up (Tasks redesign, Oct 2026).
  { key: "tasks", nav: "tasks", path: "/tasks", state: { mineOnly: true }, label: "My open tasks", Icon: IconCheckSquare, needsUser: true },
  { key: "ready_for_you", nav: "queue", path: "/queue", label: "Patients ready for you", Icon: IconCheck, providerScoped: true },
];

export default function NotificationsBell({ accessibleNavIds, onNavigate, collapsed }) {
  const { profile, practiceId } = useAuth();
  const [open, setOpen] = useState(false);
  const prevCounts = useRef(null);
  const [alertsOn, setAlertsOn] = useState(() => {
    try {
      return localStorage.getItem("pos_desktop_alerts") === "1"
        && typeof Notification !== "undefined" && Notification.permission === "granted";
    } catch (e) { return false; }
  });

  // Key the sources on the CONTENT of accessibleNavIds, not its identity.
  // Callers build the Set inline (new Set(navItems)), so its identity changes
  // on every parent render; depending on it re-ran the count queries on each
  // StaffLayout render (Oct 2026: ~6,600 requests a day, enough to time out
  // the Home RPCs). sourceKey only changes when the set of reachable queues
  // actually changes.
  const navKey = accessibleNavIds ? Array.from(accessibleNavIds).sort().join(",") : "*";
  const sourceKey = navKey + "|" + (profile?.provider_id ? "p" : "") + "|" + (profile?.role || "");
  const sources = useMemo(() => {
    const allowed = navKey === "*" ? null : new Set(navKey ? navKey.split(",") : []);
    return SOURCES.filter((s) => {
      if (s.providerScoped && !profile?.provider_id) return false;
      if (s.notRoles && s.notRoles.includes(profile?.role)) return false;
      return !allowed || allowed.has(s.nav);
    });
  }, [sourceKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const ctx = { uid: profile?.id || null, providerId: profile?.provider_id || null, role: profile?.role || null, practiceId: practiceId || null, tz: practiceTzOf(profile) };
  // A source waits (no number, no error) until the practice, and for tasks
  // the user, are known.
  const ready = (s) => !!ctx.practiceId && (!s.needsUser || !!ctx.uid);
  const countSources = sources.filter((s) => !s.shared);
  const inboxOn = sources.some((s) => s.shared === "inbox");
  const inbox = useInboxCounts({ enabled: inboxOn && !!ctx.practiceId, practiceId: ctx.practiceId });
  const results = useQueries({
    queries: countSources.map((s) => ({
      // Same key as any other component asking for this count (the sidebar
      // badge for insurance updates), so the cache answers both with one read.
      queryKey: shellKeyFor(s.key, ctx),
      queryFn: () => readShellKey(s.key, () => SHELL_READS[s.key](ctx)),
      enabled: ready(s),
      // Round 7 #2: no timer of its own. The shell's one read (useShellFeed)
      // refreshes every 60s, also in a background tab, and writes these
      // entries; that is what the desktop alert below watches.
      staleTime: 60 * 1000,
      retry: 0,
    })),
  });

  // { key: number | null } (null = not answered yet) and the sources that failed.
  const counts = {};
  const failed = [];
  sources.forEach((s) => {
    if (s.shared === "inbox") {
      counts[s.key] = inbox.value;
      if (inbox.error) failed.push(s);
      return;
    }
    const r = results[countSources.indexOf(s)] || {};
    counts[s.key] = r.data != null ? r.data : null;
    if (r.isError) failed.push(s);
  });
  const countsSig = sources.map((s) => s.key + ":" + (counts[s.key] == null ? "-" : counts[s.key])).join(",");
  const retryFailed = () => {
    failed.forEach((s) => {
      if (s.shared === "inbox") { inbox.refetch(); return; }
      const r = results[countSources.indexOf(s)];
      if (r && r.refetch) r.refetch();
    });
  };

  // Desktop alert: if a queue grew since the last answer while the tab is in
  // the background, raise a native notification. PHI-safe by construction -
  // the text is only queue labels ("open inbox items", "waitlist offers"),
  // never a patient name or any clinical detail. Never fires on the first
  // full set of counts.
  useEffect(() => {
    const next = {};
    for (const s of sources) {
      if (counts[s.key] == null) return; // wait until every source has answered
      next[s.key] = counts[s.key];
    }
    const prev = prevCounts.current;
    prevCounts.current = next;
    if (!prev || !alertsOn || typeof document === "undefined" || !document.hidden
        || typeof Notification === "undefined" || Notification.permission !== "granted") return;
    const providerId = ctx.providerId;
    // Ready-for-you gets its OWN notification with full detail (name + room),
    // because it targets the assigned provider on their authenticated device -
    // PHI is appropriate here, unlike the generic label-only path below.
    if ((next.ready_for_you || 0) > (prev.ready_for_you || 0) && providerId) {
      (async () => {
        try {
          const { data: rows, error: rowsErr } = await supabase.from("queue_entries")
            .select("patients(first_name, last_name), rooms(name), roomed_at")
            .eq("queue_status", "Roomed").eq("provider_id", providerId)
            .not("ready_for_provider_at", "is", null)
            .order("ready_for_provider_at", { ascending: false }).limit(1);
          if (rowsErr) console.warn("[NotificationsBell] ready-for-you detail lookup failed:", rowsErr.message);
          const r = rows && rows[0];
          if (r) {
            const nm = r.patients ? (r.patients.first_name + " " + (r.patients.last_name || "").charAt(0) + ".") : "A patient";
            const room = r.rooms?.name ? " in " + r.rooms.name : "";
            const n = new Notification("Ready for you", { body: nm + room, tag: "pos-ready-for-you" });
            n.onclick = () => { try { window.focus(); } catch (e) { /* ignore */ } };
          }
        } catch (e) { /* ignore */ }
      })();
    }
    // Generic PHI-free path for all other sources.
    const grew = sources.filter((s) => s.key !== "ready_for_you" && (next[s.key] || 0) > (prev[s.key] || 0));
    if (grew.length) {
      const body = grew.length === 1
        ? "New: " + grew[0].label.toLowerCase()
        : grew.length + " queues have new items needing attention";
      try {
        const n = new Notification("PracticeOS", { body, tag: "pos-attention" });
        n.onclick = () => { try { window.focus(); } catch (e) { /* ignore */ } };
      } catch (e) { /* ignore */ }
    }
  }, [countsSig, alertsOn]); // eslint-disable-line react-hooks/exhaustive-deps

  // Opt-in toggle for desktop alerts. Turning on requests browser permission;
  // the preference is remembered per device.
  const toggleAlerts = async () => {
    if (alertsOn) {
      setAlertsOn(false);
      try { localStorage.setItem("pos_desktop_alerts", "0"); } catch (e) { /* ignore */ }
      return;
    }
    if (typeof Notification === "undefined") {
      toast({ kind: "warn", text: "This browser does not support desktop alerts." });
      return;
    }
    let perm = Notification.permission;
    if (perm !== "granted") { try { perm = await Notification.requestPermission(); } catch (e) { perm = "denied"; } }
    if (perm === "granted") {
      setAlertsOn(true);
      try { localStorage.setItem("pos_desktop_alerts", "1"); } catch (e) { /* ignore */ }
      toast({ kind: "success", text: "Desktop alerts on. You'll be notified of new items when this tab is in the background." });
    } else {
      toast({ kind: "warn", text: "Desktop alerts are blocked in your browser settings." });
    }
  };

  const total = sources.reduce((a, s) => a + (counts[s.key] || 0), 0);
  const actionable = sources.filter((s) => (counts[s.key] || 0) > 0);

  const go = (s) => { setOpen(false); onNavigate(s.path, s.state); };

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        title="Notifications"
        aria-label={total > 0 ? "Notifications, " + total + " waiting" : "Notifications"}
        data-testid="bell-button"
        style={{
          position: "relative", flexShrink: 0,
          width: 36, height: 34, display: "flex", alignItems: "center", justifyContent: "center",
          /* dark-mode-ignore-start: sidebar chrome, always dark navy in both themes */
          background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.72)",
          /* dark-mode-ignore-end */
          /* dark-mode-ignore-start: sidebar chrome, always dark navy in both themes */
          border: "0.5px solid rgba(255,255,255,0.14)", borderRadius: 8, cursor: "pointer",
          /* dark-mode-ignore-end */
        }}
      >
        <IconBell size={16} />
        {total > 0 && (
          <span data-testid="bell-total" style={{
            position: "absolute", top: -5, right: -5,
            minWidth: 15, height: 15, padding: "0 4px", borderRadius: 8,
            background: C.dangerSolid, color: C.onAccent, fontSize: 9, fontWeight: 700,
            display: "flex", alignItems: "center", justifyContent: "center",
            lineHeight: 1, boxSizing: "border-box", border: "1.5px solid #0a0a0a",
          }}>{total > 99 ? "99+" : total}</span>
        )}
      </button>

      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 349 }} />
          <div data-testid="bell-panel" style={{
            position: "fixed", top: 100, left: collapsed ? 70 : 14, width: 300, maxWidth: "calc(100vw - 28px)", zIndex: 350,
            background: C.bgPrimary, borderRadius: 12, border: "0.5px solid " + C.borderLight,
            boxShadow: C.shadowLg, overflow: "hidden", animation: "posModalIn 0.16s cubic-bezier(0.16,1,0.3,1)",
          }}>
            <div style={{
              padding: "11px 14px", borderBottom: "0.5px solid " + C.borderLight,
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: C.textPrimary }}>Needs your attention</span>
              {total > 0 && <span style={{ fontSize: 11, color: C.textTertiary }}>{total} item{total === 1 ? "" : "s"}</span>}
            </div>

            {failed.length > 0 && (
              <div data-testid="bell-error" role="alert" style={{
                display: "flex", alignItems: "center", gap: 8, padding: "9px 14px",
                background: C.warnBg, color: C.warnFg, fontSize: 12, borderBottom: "0.5px solid " + C.borderLight,
              }}>
                <span style={{ flex: 1 }}>Couldn't load {failed.length === 1 ? failed[0].label.toLowerCase() : failed.length + " counts"}.</span>
                <button onClick={retryFailed} style={{
                  background: "transparent", border: "0.5px solid " + C.borderMid, borderRadius: 6, color: C.warnFg,
                  fontSize: 11, fontWeight: 700, padding: "3px 10px", cursor: "pointer", fontFamily: "inherit",
                }}>Retry</button>
              </div>
            )}

            {actionable.length === 0 ? (
              failed.length > 0 ? null : (
              <div style={{ padding: "26px 16px", textAlign: "center", color: C.textTertiary }}>
                <div style={{ display: "inline-flex", marginBottom: 8, color: "var(--brand-text, " + C.tealMid + ")" }}><IconCheck size={24} /></div>
                <div style={{ fontSize: 13, color: C.textSecondary, fontWeight: 600 }}>You are all caught up</div>
                <div style={{ fontSize: 11, marginTop: 2 }}>No queues need attention right now.</div>
              </div>
              )
            ) : (
              <div style={{ padding: "4px 0", maxHeight: "60vh", overflowY: "auto" }}>
                {actionable.map((s) => (
                  <button key={s.key} data-testid={"bell-row-" + s.key} onClick={() => go(s)}
                    onMouseEnter={(e) => (e.currentTarget.style.background = C.bgSecondary)}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    style={{
                      width: "100%", display: "flex", alignItems: "center", gap: 11, padding: "10px 14px",
                      background: "transparent", border: "none", cursor: "pointer", textAlign: "left",
                      fontFamily: "inherit", transition: "background 0.12s",
                    }}>
                    <span style={{ display: "inline-flex", color: C.textSecondary, flexShrink: 0 }}><s.Icon size={17} /></span>
                    <span style={{ flex: 1, fontSize: 13, color: C.textPrimary }}>{s.label}</span>
                    <span data-testid={"bell-count-" + s.key} style={{
                      minWidth: 20, height: 20, padding: "0 6px", borderRadius: 10,
                      background: "var(--brand-bg, " + C.tealBg + ")", color: C.brandText,
                      fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", boxSizing: "border-box",
                    }}>{counts[s.key]}</span>
                  </button>
                ))}
              </div>
            )}

            <div style={{ padding: "9px 14px", borderTop: "0.5px solid " + C.borderLight, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 12, color: C.textSecondary }}>Desktop alerts</span>
              <button onClick={toggleAlerts} style={{
                fontSize: 11, fontWeight: 700, padding: "4px 12px", borderRadius: 12, cursor: "pointer", fontFamily: "inherit",
                border: "0.5px solid " + (alertsOn ? "var(--brand-primary, " + C.teal + ")" : C.borderMid),
                background: alertsOn ? "var(--brand-bg, " + C.tealBg + ")" : "transparent",
                color: alertsOn ? C.brandText : C.textSecondary,
              }}>{alertsOn ? "On" : "Off"}</button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
